'use client';

import { useState } from 'react';
import { WatchLaterButton } from '../islands/WatchLaterButton';

/**
 * Cluster of secondary actions for a video — Like, Share, Save
 * (watch-later). Like is a local-only optimistic counter (operator
 * UX); Save is the {@link WatchLaterButton} island; Share copies
 * the {@code shareUrl} to clipboard.
 */
export interface VideoActionGroupProps {
  videoSlug: string;
  shareUrl?: string;
  initialLikes?: number;
  showShare?: boolean;
  showLike?: boolean;
  showSave?: boolean;
}

export function VideoActionGroup({
  videoSlug,
  shareUrl,
  initialLikes = 0,
  showShare = true,
  showLike = true,
  showSave = true,
}: VideoActionGroupProps) {
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);
  const likeCount = initialLikes + (liked ? 1 : 0);

  const share = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {showLike && (
        <button
          type="button"
          onClick={() => setLiked((l) => !l)}
          aria-pressed={liked}
          style={pillStyle(liked)}
        >
          {liked ? '♥' : '♡'} {likeCount > 0 ? likeCount.toLocaleString() : 'Like'}
        </button>
      )}
      {showSave && <WatchLaterButton videoSlug={videoSlug} />}
      {showShare && (
        <button type="button" onClick={share} style={pillStyle(false)}>
          {copied ? '✓ Copied' : '↗ Share'}
        </button>
      )}
    </div>
  );
}

function pillStyle(active: boolean) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: 'var(--space-2, 8px) var(--space-3-5, 14px)',
    borderRadius: 999,
    fontSize: '13px',
    fontWeight: 600,
    border:
      '1px solid ' +
      (active ? 'var(--color-brand-500, #3b5bdb)' : 'var(--color-border, rgba(0,0,0,0.12))'),
    background: active ? 'var(--color-brand-50, #eff6ff)' : 'var(--color-surface-card, #fff)',
    color: active ? 'var(--color-brand-700, #1d4ed8)' : 'var(--color-text-primary, #18181b)',
    cursor: 'pointer',
  };
}
